import React, {useState, useEffect, useMemo, useRef } from 'react'
import {Box, Button, Flex, Heading, Text} from '@salesforce/retail-react-app/app/components/shared/ui';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

/* UTILS */
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import isEqual from 'lodash/isEqual';

/* Component */
import StarRating from './star-rating';
import ActualFit from './actual-fit';
import WriteReview from './write-review';
import CustomerSay from './customer-say';

/* Constants */
const VIEW_TYPE = 'small'

/* API Hooks */
import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import {useReviewSummary} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'
import {useBrand} from '../../../contexts/brand-context'

import {sendWriteReviewEvent} from '../../../utils/gtm-utils'

const ReviewsHeadline = ({ product, isLoading, reviewFormData, noReviews, configs = {} }) => {
    const {variationParams} = useDerivedProduct(product)
    const {isGarage} = useBrand()

    const [isWriteReviewOpenModal, setIsWriteReviewOpenModal] = useState(false);
    const [selectedVariationParams, setSelectedVariationParams] = useState(variationParams)
    const writeReviewButtonRef = useRef(null);

    const pid = product?.master?.masterId || product?.id;
    const totalReviews =
        product?.c_bvReviewCount != null ? Number(product.c_bvReviewCount) : undefined;
    const averageRating = product?.c_bvAverageRating?.toString();
    const fitValue = product?.c_bvHowDoesItFitAverageRating;
    const imageGroups = product?.imageGroups;

    const {summary: customerSaySummary} = useReviewSummary({pid});
    const customerSay = !!customerSaySummary;

    const defaultConfigs = {
        showRecommendations: false,
        showTotalReviews: true
    }
    const mergedConfigs = {
        showRecommendations: configs?.showRecommendations ?? defaultConfigs.showRecommendations,
        showTotalReviews: configs?.showTotalReviews ?? defaultConfigs.showTotalReviews
    }

    const handleWriteAReview = () => {
        setIsWriteReviewOpenModal(true);
        // GTM Modification
        sendWriteReviewEvent(product)
    }

    const imageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: VIEW_TYPE,
                selectedVariationAttributes: selectedVariationParams
            }),
        [imageGroups, selectedVariationParams]
    )

    useEffect(() => {
        if (!isEqual(selectedVariationParams, variationParams)) {
            setSelectedVariationParams(variationParams);
        }
    }, [variationParams, selectedVariationParams]);

    return (
        <Box
            bgColor="webTheme.backgroundPrimaryBrand"
            padding={{base: '32px 16px', lg: customerSay || noReviews ? '32px' : '32px 72px'}}
        >
            <Flex>
                <Box flexBasis="100%">
                    {noReviews && (
                        <Box>
                            {/* Title */}
                            <Text as="h2" textStyle="webH2" mb={{base: 4, lg: 4}}>
                                {noReviews && (
                                    <FormattedMessage
                                        defaultMessage="Review"
                                        id="pdp_reviews.headline.title.no.reviews"
                                    />
                                )}
                                {!noReviews && (
                                    <FormattedMessage
                                        defaultMessage="Reviews"
                                        id="pdp_reviews.headline.title"
                                    />
                                )}
                            </Text>
                            <Text textStyle="bodySmRegular" mb={8}>
                                {isGarage ? (
                                    <FormattedMessage
                                        defaultMessage="Be the first to review this product."
                                        id="pdp_reviews.headline.sub.title.no.reviews.garage"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Be the first to review this product."
                                        id="pdp_reviews.headline.sub.title.no.reviews.dynamite"
                                    />
                                )}
                            </Text>
                        </Box>
                    )}
                    <Flex flexWrap={{base: 'wrap', lg: 'nowrap'}} gap={{lg: '48px'}}>
                        {/* Write a review btn */}
                        {noReviews && (
                            <Button
                                id="pdp-reviews"
                                isLoading={isLoading}
                                variant="webPrimary"
                                width="fit-content"
                                minWidth="auto"
                                px={6}
                                height={10}
                                minHeight={10}
                                onClick={handleWriteAReview}
                                ref={writeReviewButtonRef}
                                aria-label="Write a review"
                                _focusVisible={{
                                    outline: '2px solid',
                                    outlineColor: 'webTheme.focus',
                                    outlineOffset: '2px'
                                }}
                            >
                                <FormattedMessage
                                    defaultMessage="Write a review"
                                    id="pdp_reviews.write.review"
                                />
                            </Button>
                        )}
                        {!noReviews && (
                            <Box
                                flex={{base: '0 0 100%', lg: '0 0 259px'}}
                                mt={{base: 0}}
                                mb={{base: 9, lg: 0}}
                            >
                                {/* Title */}
                                <Text as="h2" textStyle="webH2" mb={{base: 4, lg: 8}}>
                                    {noReviews && (
                                        <FormattedMessage
                                            defaultMessage="Review"
                                            id="pdp_reviews.headline.title.no.reviews"
                                        />
                                    )}
                                    {!noReviews && (
                                        <FormattedMessage
                                            defaultMessage="Reviews"
                                            id="pdp_reviews.headline.title"
                                        />
                                    )}
                                </Text>
                                <Flex flexWrap={{base: 'wrap', lg: 'wrap'}}>
                                    {averageRating && (
                                        <Box flex="0 0 100%" mb={!customerSay ? 0 : 8}>
                                            <Text
                                                fontSize={{base: '40px', lg: '64px'}}
                                                lineHeight={{base: '48px', lg: '64px'}}
                                                textTransform="uppercase"
                                                fontWeight={700}
                                                mb={{base: 1, lg: 2}}
                                            >
                                                {Number(averageRating).toFixed(1)}
                                            </Text>
                                            <StarRating
                                                initialRating={Number(averageRating)}
                                                lock={true}
                                                styles={{
                                                    starBgColor: '#F0EFEB'
                                                }}
                                                totalReviews={totalReviews}
                                                showTotalReviews={mergedConfigs.showTotalReviews}
                                            />
                                        </Box>
                                    )}

                                    {mergedConfigs.showRecommendations && (
                                        <Box flexBasis={{base: '50%', lg: '100%'}}>
                                            <Text
                                                fontSize="32px"
                                                lineHeight="34px"
                                                textTransform="uppercase"
                                                fontWeight={700}
                                                mb={{base: 1, lg: 2}}
                                            >
                                                96%
                                            </Text>
                                            <Text
                                                fontSize="xs"
                                                lineHeight="15px"
                                                pr={{base: 0, lg: 4}}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="of customers recommend this product"
                                                    id="pdp_reviews.headline.recommended"
                                                />
                                            </Text>
                                        </Box>
                                    )}

                                    {/* Fit bar - only shown in left column when AI summary is present */}
                                    {customerSay && fitValue && !noReviews && (
                                        <Box flex="0 0 100%">
                                            <Text as="h3" textStyle="webH3" mb={4}>
                                                <FormattedMessage
                                                    defaultMessage="Actual Fit"
                                                    id="pdp_reviews.description.actualfit"
                                                />
                                            </Text>
                                            <ActualFit fit={fitValue} />
                                        </Box>
                                    )}
                                </Flex>
                            </Box>
                        )}

                        {/* Customer say */}
                        <Box width="100%">
                            {customerSay && !noReviews && (
                                <CustomerSay summary={customerSaySummary} />
                            )}

                            {/* Actual Fit - moved above CTA when no AI summary */}
                            {!customerSay && fitValue && !noReviews && (
                                <Box mb={{base: 8, lg: 8}}>
                                    <Text as="h3" textStyle="webH3" mb={4}>
                                        <FormattedMessage
                                            defaultMessage="Actual Fit"
                                            id="pdp_reviews.description.actualfit"
                                        />
                                    </Text>
                                    <ActualFit fit={fitValue} />
                                </Box>
                            )}

                            {/* Write a review btn */}
                            {!noReviews && (
                                <Button
                                    id="pdp-reviews"
                                    isLoading={isLoading}
                                    variant="webPrimary"
                                    width="fit-content"
                                    minWidth="auto"
                                    px={6}
                                    onClick={handleWriteAReview}
                                    ref={writeReviewButtonRef}
                                    aria-label="Write a review"
                                    _focusVisible={{
                                        outline: '2px solid',
                                        outlineColor: 'webTheme.focus',
                                        outlineOffset: '2px'
                                    }}
                                >
                                    <FormattedMessage
                                        defaultMessage="Write a review"
                                        id="pdp_reviews.write.review"
                                    />
                                </Button>
                            )}

                            {/* Write review modal */}
                            <WriteReview
                                isOpen={isWriteReviewOpenModal}
                                onClose={() => setIsWriteReviewOpenModal(false)}
                                onOpenWriteReview={() => setIsWriteReviewOpenModal(true)}
                                reviewFormData={reviewFormData}
                                images={imageGroup?.images}
                                product={product}
                                writeReviewButtonRef={writeReviewButtonRef}
                            />
                        </Box>
                    </Flex>
                </Box>
            </Flex>
        </Box>
    )
}

ReviewsHeadline.propTypes = {
    product: PropTypes.object,
    isLoading: PropTypes.bool,
    reviewFormData: PropTypes.object,
    configs: PropTypes.object,
    noReviews: PropTypes.bool
}

export default ReviewsHeadline
